import { useState, useRef, useEffect } from 'react';
import { client } from '@/api/client';
import { toast } from 'sonner';
import { CheckCircle2, Circle, AlertCircle, Save, Upload, FileText, ExternalLink, ChevronDown, Play, ClipboardList, History, FileCheck, ShieldOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import GoodFaithModal from './GoodFaithModal';
import SignaturePad from './SignaturePad';
import DriverApplicationModal from './DriverApplicationModal';
import RoadTestCertificateModal from './RoadTestCertificateModal';
import RoadTestExaminationModal from './RoadTestExaminationModal';
import PreviousEmploymentSection from './PreviousEmploymentSection';

export const DQF_ITEMS = [
  { key: 'dqf_annual_mvr',               label: 'Annual MVR',                          required: false },
  { key: 'dqf_medical_card',             label: 'Medical Card',                        required: false },
  { key: 'dqf_fmcsa_crash_report',       label: 'FMCSA Crash & Inspection Report',     required: false, optional: true },
  { key: 'dqf_driver_application',       label: 'Driver Application',                  required: false },
  { key: 'dqf_initial_mvr',             label: 'Initial Driver MVR (Preceding 3 Yrs)', required: false },
  { key: 'dqf_road_test',               label: 'Road Test',                            required: false },
  { key: 'dqf_prev_employer_verification', label: 'Previous Employer Verification',    required: false },
  { key: 'dqf_alcohol_drug_statement',   label: 'Alcohol & Drug Test Statement',       required: false },
  { key: 'dqf_drug_test',               label: 'Pre-Employment Drug Test',             required: true },
  { key: 'dqf_clearinghouse',           label: 'Clearinghouse Query',                  required: true },
  { key: 'dqf_random_program',          label: 'Random Program',                       required: false },
  { key: 'dqf_policies_consents',       label: 'Driver Policies & Consents',           required: false },
];

export default function DQFChecklist({ driver, onSaved }) {
  const [checks, setChecks] = useState(() => {
    const init = {};
    DQF_ITEMS.forEach(item => { init[item.key] = driver[item.key] ?? false; });
    return init;
  });
  const [saving, setSaving] = useState(false);
  const [mvrExpanded, setMvrExpanded] = useState(false);
  const [medCardExpanded, setMedCardExpanded] = useState(false);
  const [showGoodFaith, setShowGoodFaith] = useState(false);
  const [mvrNotes, setMvrNotes] = useState(driver.mvr_notes || '');
  const [mvrReviewDate, setMvrReviewDate] = useState(driver.mvr_review_date || '');
  const [mvrReviewedBy, setMvrReviewedBy] = useState(driver.mvr_reviewed_by || '');
  const [mvrDocUrl, setMvrDocUrl] = useState(driver.mvr_document_url || '');
  const [uploadingMvr, setUploadingMvr] = useState(false);
  // Medical Card state
  const [medIssueDate, setMedIssueDate] = useState(driver.med_card_issue_date || '');
  const [medExpDate, setMedExpDate] = useState(driver.med_card_expiration_date || '');
  const [medExaminerName, setMedExaminerName] = useState(driver.med_card_examiner_name || '');
  const [medRegistryNumber, setMedRegistryNumber] = useState(driver.med_card_registry_number || '');
  const [medDocUrl, setMedDocUrl] = useState(driver.med_card_document_url || '');
  const [medVerified, setMedVerified] = useState(driver.med_card_verified || false);
  const [medVerifiedBy, setMedVerifiedBy] = useState(driver.med_card_verified_by || '');
  const [medSignature, setMedSignature] = useState(driver.med_card_signature || '');
  const [uploadingMed, setUploadingMed] = useState(false);
  // Driver Application state
  const [appExpanded, setAppExpanded] = useState(false);
  const [appDocUrl, setAppDocUrl] = useState(driver.driver_application_url || '');
  const [uploadingApp, setUploadingApp] = useState(false);
  const [showAppForm, setShowAppForm] = useState(false);
  const [showRoadTestCert, setShowRoadTestCert] = useState(false);
  const [showRoadTestExam, setShowRoadTestExam] = useState(false);
  // Road Test state
  const [roadTestExpanded, setRoadTestExpanded] = useState(false);
  // Prev Employer state
  const [prevEmployerExpanded, setPrevEmployerExpanded] = useState(false);
  // Alcohol & Drug Statement state
  const [alcoholDrugExpanded, setAlcoholDrugExpanded] = useState(false);
  // Drug Test state
  const [drugTestExpanded, setDrugTestExpanded] = useState(false);
  // Random Program state
  const [randomProgramExpanded, setRandomProgramExpanded] = useState(false);
  const [randomProgramDate, setRandomProgramDate] = useState(driver.random_program_date || '');
  // Policies & Consents state
  const [policiesExpanded, setPoliciesExpanded] = useState(false);
  const parsedPolicySignatures = (() => {
    try { return driver.policy_signatures ? JSON.parse(driver.policy_signatures) : {}; }
    catch { return {}; }
  })();
  // Clearinghouse state
  const [clearinghouseExpanded, setClearinghouseExpanded] = useState(false);
  const [clearinghouseExempt, setClearinghouseExempt] = useState(driver.clearinghouse_exempt || false);
  const [clearinghouseQueryDate, setClearinghouseQueryDate] = useState(driver.clearinghouse_query_date || '');
  const [clearinghouseResult, setClearinghouseResult] = useState(driver.clearinghouse_result || 'Not Prohibited');
  const [drugTestDocUrl, setDrugTestDocUrl] = useState(driver.drug_test_doc_url || '');
  const [drugTestChainUrl, setDrugTestChainUrl] = useState(driver.drug_test_chain_url || '');
  const [uploadingDrugTest, setUploadingDrugTest] = useState(false);
  const [uploadingDrugChain, setUploadingDrugChain] = useState(false);
  const [roadTestDocUrl, setRoadTestDocUrl] = useState(driver.road_test_doc_url || '');
  const [uploadingRoadTest, setUploadingRoadTest] = useState(false);
  // CDL Document state
  const [cdlDocUrl, setCdlDocUrl] = useState(driver.cdl_document_url || '');
  // Initial MVR state
  const [initialMvrExpanded, setInitialMvrExpanded] = useState(false);
  const [initialMvrStatus, setInitialMvrStatus] = useState(driver.dqf_initial_mvr ? 'Completed' : 'Pending');
  const [initialMvrDocUrl, setInitialMvrDocUrl] = useState(driver.initial_mvr_doc_url || '');
  const [uploadingInitialMvr, setUploadingInitialMvr] = useState(false);
  // Crash Report state
  const [crashExpanded, setCrashExpanded] = useState(false);
  const [crashStatus, setCrashStatus] = useState(driver.crash_report_status || 'Incomplete');
  const [crashDocUrl, setCrashDocUrl] = useState(driver.crash_report_url || '');
  const [uploadingCrash, setUploadingCrash] = useState(false);
  const [crashActionOpen, setCrashActionOpen] = useState(false);
  const crashActionRef = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (crashActionRef.current && !crashActionRef.current.contains(e.target)) setCrashActionOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggle = (key) => setChecks(prev => ({ ...prev, [key]: !prev[key] }));

  const markMvrComplete = (extra = {}) => {
    setChecks(prev => ({ ...prev, dqf_annual_mvr: true }));
    handleSave({ dqf_annual_mvr: true, ...extra });
  };

  const handleMedCardUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingMed(true);
    toast.info('Uploading Medical Card...');
    const { file_url } = await client.integrations.Core.UploadFile({ file });
    setMedDocUrl(file_url);
    setUploadingMed(false);
    toast.success('Medical Card uploaded.');
  };

  const handleMvrUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingMvr(true);
    toast.info('Uploading MVR document...');
    const { file_url } = await client.integrations.Core.UploadFile({ file });
    setMvrDocUrl(file_url);
    markMvrComplete({ mvr_document_url: file_url });
    setUploadingMvr(false);
    toast.success('MVR document uploaded — Annual MVR marked complete.');
  };

  const handleAppUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingApp(true);
    toast.info('Uploading Driver Application...');
    const { file_url } = await client.integrations.Core.UploadFile({ file });
    setAppDocUrl(file_url);
    setChecks(prev => ({ ...prev, dqf_driver_application: true }));
    setUploadingApp(false);
    toast.success('Driver Application uploaded — marked complete.');
    handleSave({ driver_application_url: file_url, dqf_driver_application: true });
  };

  const handleCrashUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingCrash(true);
    toast.info('Uploading crash report...');
    const { file_url } = await client.integrations.Core.UploadFile({ file });
    setCrashDocUrl(file_url);
    setCrashStatus('Complete');
    setChecks(prev => ({ ...prev, dqf_fmcsa_crash_report: true }));
    setUploadingCrash(false);
    toast.success('Crash report uploaded — marked complete.');
    handleSave({ crash_report_url: file_url, crash_report_status: 'Complete', dqf_fmcsa_crash_report: true });
  };

  // Every "mark complete" action across this checklist used to only update
  // local `checks` (or other local field) state and rely on a single,
  // separate top-level Save click to actually persist anything. That
  // second click was easy to miss -- each action button/upload reads like
  // it already saved on its own -- so a driver's DQF items could look
  // checked in the UI but silently never reach the database, then revert
  // to unchecked on the next load/refresh. `overrides` lets every call site
  // pass the exact field(s) it just set so this always persists them
  // immediately instead of waiting on a second click and a batched (and
  // therefore possibly stale) `checks`/other state snapshot.
  const handleSave = async (overrides = {}) => {
    setSaving(true);
    const updates = {
      ...checks,
      crash_report_status: crashStatus,
      crash_report_url: crashDocUrl,
      mvr_document_url: mvrDocUrl,
      mvr_notes: mvrNotes,
      mvr_review_date: mvrReviewDate,
      mvr_reviewed_by: mvrReviewedBy,
      med_card_issue_date: medIssueDate,
      med_card_expiration_date: medExpDate,
      med_card_examiner_name: medExaminerName,
      med_card_registry_number: medRegistryNumber,
      med_card_document_url: medDocUrl,
      med_card_verified: medVerified,
      med_card_verified_by: medVerifiedBy,
      med_card_signature: medSignature,
      driver_application_url: appDocUrl,
      cdl_document_url: cdlDocUrl,
      clearinghouse_exempt: clearinghouseExempt,
      clearinghouse_query_date: clearinghouseQueryDate,
      clearinghouse_result: clearinghouseResult,
      drug_test_doc_url: drugTestDocUrl,
      drug_test_chain_url: drugTestChainUrl,
      road_test_doc_url: roadTestDocUrl,
      initial_mvr_doc_url: initialMvrDocUrl,
      random_program_date: randomProgramDate,
      ...overrides,
    };
    try {
      await client.entities.DriverProfile.update(driver.id, updates);
    } catch {
      setSaving(false);
      toast.error('Could not save DQF checklist. Please try again.');
      return;
    }
    toast.success('DQF checklist saved.');
    setSaving(false);
    if (onSaved) onSaved(updates);
  };

  const getExpiryBadge = (dateStr) => {
    if (!dateStr) return null;
    const days = Math.ceil((new Date(dateStr) - new Date()) / (1000 * 60 * 60 * 24));
    if (days > 30) return null;
    const color = days <= 0 ? 'bg-red-500/20 text-red-400 border-red-500/40'
      : days <= 7 ? 'bg-red-500/20 text-red-400 border-red-500/40'
      : days <= 14 ? 'bg-orange-500/20 text-orange-400 border-orange-500/40'
      : 'bg-amber-500/20 text-amber-400 border-amber-500/40';
    const label = days <= 0 ? 'EXPIRED' : `Expires in ${days}d`;
    return <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${color}`}>{label}</span>;
  };

  const cdlExpiryBadge = getExpiryBadge(driver.cdl_expiration_date);
  const medExpiryBadge = getExpiryBadge(medExpDate || driver.med_card_expiration_date);

  const requiredMet = DQF_ITEMS.filter(i => i.required).every(i => checks[i.key]);
  const completedCount = DQF_ITEMS.filter(i => checks[i.key]).length;

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold text-primary uppercase tracking-widest">DQF Requirements</p>
          <p className="text-xs text-muted-foreground mt-0.5">{completedCount} of {DQF_ITEMS.length} completed</p>
        </div>
        <Button size="sm" onClick={handleSave} disabled={saving} className="h-7 px-3 text-xs bg-primary text-primary-foreground">
          <Save className="w-3 h-3 mr-1" />
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </div>

      {/* Required warning */}
      {!requiredMet && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/20 text-xs text-red-400">
          <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
          <span>Clearinghouse Query and Pre-Employment Drug Test are required before approval.</span>
        </div>
      )}
      {requiredMet && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-green-500/10 border border-green-500/20 text-xs text-green-400">
          <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" />
          <span>Mandatory requirements met — driver can be approved.</span>
        </div>
      )}

      {/* Checklist */}
      <div className="rounded-xl border border-border overflow-hidden divide-y divide-border">
        {DQF_ITEMS.map(item => {
          const checked = checks[item.key];
          const isMvr = item.key === 'dqf_annual_mvr';
          const isMedCard = item.key === 'dqf_medical_card';
          const isCrash = item.key === 'dqf_fmcsa_crash_report';
          const isApp = item.key === 'dqf_driver_application';
          const isInitialMvr = item.key === 'dqf_initial_mvr';
          const isRoadTest = item.key === 'dqf_road_test';
          const isPrevEmployer = item.key === 'dqf_prev_employer_verification';
          const isAlcoholDrug = item.key === 'dqf_alcohol_drug_statement';
          const isDrugTest = item.key === 'dqf_drug_test';
          const isClearinghouse = item.key === 'dqf_clearinghouse';
          const isRandomProgram = item.key === 'dqf_random_program';
          const isPolicies = item.key === 'dqf_policies_consents';
          const isExpandable = isMvr || isMedCard || isCrash || isApp || isInitialMvr || isRoadTest || isPrevEmployer || isAlcoholDrug || isDrugTest || isClearinghouse || isRandomProgram || isPolicies;
          const isExpanded = isMvr ? mvrExpanded : isMedCard ? medCardExpanded : isCrash ? crashExpanded : isApp ? appExpanded : isInitialMvr ? initialMvrExpanded : isRoadTest ? roadTestExpanded : isPrevEmployer ? prevEmployerExpanded : isAlcoholDrug ? alcoholDrugExpanded : isDrugTest ? drugTestExpanded : isClearinghouse ? clearinghouseExpanded : isRandomProgram ? randomProgramExpanded : isPolicies ? policiesExpanded : false;
          return (
            <div key={item.key}>
              <button
                type="button"
                onClick={() => {
                  if (isMvr) setMvrExpanded(prev => !prev);
                  else if (isMedCard) setMedCardExpanded(prev => !prev);
                  else if (isCrash) setCrashExpanded(prev => !prev);
                  else if (isApp) setAppExpanded(prev => !prev);
                  else if (isInitialMvr) setInitialMvrExpanded(prev => !prev);
                  else if (isRoadTest) setRoadTestExpanded(prev => !prev);
                  else if (isPrevEmployer) setPrevEmployerExpanded(prev => !prev);
                  else if (isAlcoholDrug) setAlcoholDrugExpanded(prev => !prev);
                  else if (isDrugTest) setDrugTestExpanded(prev => !prev);
                  else if (isClearinghouse) setClearinghouseExpanded(prev => !prev);
                  else if (isRandomProgram) setRandomProgramExpanded(prev => !prev);
                  else if (isPolicies) setPoliciesExpanded(prev => !prev);
                  else toggle(item.key);
                }}
                className={cn(
                  "w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-white/5",
                  checked ? "bg-green-500/5" : "bg-transparent"
                )}
              >
                {checked
                  ? <CheckCircle2 className="w-4 h-4 text-green-400 flex-shrink-0" />
                  : <Circle className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                }
                <span className={cn("text-sm flex-1", checked ? "text-foreground" : "text-muted-foreground")}>
                  {item.label}
                </span>
                {isMvr && cdlExpiryBadge}
                {isMedCard && medExpiryBadge}
                {item.required && (
                  <span className="text-xs font-semibold text-red-400 bg-red-500/10 border border-red-500/20 px-2 py-0.5 rounded-full">
                    Required
                  </span>
                )}
                {item.optional && (
                  <span className="text-xs text-muted-foreground">(optional)</span>
                )}
                {isExpandable && (
                  <ChevronDown className={cn("w-3.5 h-3.5 text-muted-foreground transition-transform ml-1", isExpanded && "rotate-180")} />
                )}
              </button>

              {/* Medical Card expanded section */}
              {isMedCard && medCardExpanded && (
                <div className="px-4 pb-4 pt-3 bg-black/20 border-t border-border space-y-3">
                  <p className="text-xs text-muted-foreground font-semibold uppercase tracking-widest">49 CFR 391.43 — Copy of Medical Certificate</p>

                  {/* Issue & Expiration */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Issue Date</p>
                      <input type="date" value={medIssueDate} onChange={e => setMedIssueDate(e.target.value)}
                        className="w-full px-3 py-1.5 rounded-lg bg-transparent border border-input text-foreground text-xs focus:outline-none focus:ring-1 focus:ring-primary/50" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Expiration Date</p>
                      <input type="date" value={medExpDate} onChange={e => setMedExpDate(e.target.value)}
                        className="w-full px-3 py-1.5 rounded-lg bg-transparent border border-input text-foreground text-xs focus:outline-none focus:ring-1 focus:ring-primary/50" />
                    </div>
                  </div>

                  {/* Examiner & Registry */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Medical Examiner</p>
                      <input type="text" value={medExaminerName} onChange={e => setMedExaminerName(e.target.value)}
                        placeholder="e.g. Michelle Guidry L."
                        className="w-full px-3 py-1.5 rounded-lg bg-transparent border border-input text-foreground text-xs placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">National Registry Number</p>
                      <input type="text" value={medRegistryNumber} onChange={e => setMedRegistryNumber(e.target.value)}
                        placeholder="e.g. 1278764696"
                        className="w-full px-3 py-1.5 rounded-lg bg-transparent border border-input text-foreground text-xs placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50" />
                    </div>
                  </div>

                  {/* Verification section */}
                  <div className={cn("rounded-lg border p-3 space-y-3 transition-colors", medVerified ? "border-green-500/40 bg-green-500/5" : "border-border bg-muted/40")}>
                    <button
                      type="button"
                      onClick={() => setMedVerified(v => !v)}
                      className="flex items-center gap-3 w-full text-left"
                    >
                      {medVerified
                        ? <CheckCircle2 className="w-4 h-4 text-green-400 flex-shrink-0" />
                        : <Circle className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      }
                      <p className={cn("text-xs font-medium", medVerified ? "text-green-400" : "text-muted-foreground")}>
                        Verification for the Medical Examiner
                      </p>
                    </button>

                    {/* Verified by & Signature */}
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Verified By <span className="text-red-400">*</span></p>
                      <input
                        type="text"
                        value={medVerifiedBy}
                        onChange={e => setMedVerifiedBy(e.target.value)}
                        placeholder="Full name of person who verified"
                        className="w-full px-3 py-1.5 rounded-lg bg-transparent border border-input text-foreground text-xs placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
                      />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Signature <span className="text-red-400">*</span></p>
                      <SignaturePad value={medSignature} onChange={setMedSignature} />
                    </div>

                    {medVerified && medVerifiedBy && (
                      <p className="text-xs text-green-400/80">Verification completed by {medVerifiedBy}.</p>
                    )}
                  </div>

                  {/* Upload */}
                  <div>
                    <p className="text-xs text-muted-foreground mb-1.5">Upload Medical Card Document</p>
                    <label className={cn(
                      "flex items-center gap-3 px-3 py-2.5 rounded-lg border cursor-pointer transition-colors",
                      medDocUrl ? "border-green-500/40 bg-green-500/5" : "border-border bg-muted/40 hover:border-primary/40"
                    )}>
                      <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png" onChange={handleMedCardUpload} disabled={uploadingMed} />
                      {uploadingMed ? (
                        <div className="w-4 h-4 border-2 border-border border-t-primary rounded-full animate-spin flex-shrink-0" />
                      ) : medDocUrl ? (
                        <FileText className="w-4 h-4 text-green-400 flex-shrink-0" />
                      ) : (
                        <Upload className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      )}
                      <span className={cn("text-xs truncate flex-1", medDocUrl ? "text-green-400" : "text-muted-foreground")}>
                        {uploadingMed ? 'Uploading…' : medDocUrl ? 'Medical Card uploaded — click to replace' : 'Upload Medical Card (PDF, JPG, PNG)  — File Side: Front'}
                      </span>
                      {medDocUrl && (
                        <a href={medDocUrl} target="_blank" rel="noopener noreferrer"
                          onClick={e => e.stopPropagation()}
                          className="text-blue-400 hover:text-blue-300 flex-shrink-0">
                          <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      )}
                    </label>
                  </div>

                  {/* Mark complete button */}
                  <button
                    type="button"
                    onClick={() => { setChecks(prev => ({ ...prev, dqf_medical_card: true })); toast.success('Medical Card marked complete.'); handleSave({ dqf_medical_card: true }); }}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary/10 border border-primary/30 text-xs text-primary hover:bg-primary/20 transition-colors"
                  >
                    <CheckCircle2 className="w-3 h-3" />
                    Mark Medical Card Complete
                  </button>
                </div>
              )}

              {/* Crash Report expanded section */}
              {isCrash && crashExpanded && (
                <div className="px-4 pb-4 pt-3 bg-black/20 border-t border-border space-y-3">
                  <p className="text-xs text-muted-foreground font-semibold uppercase tracking-widest">FMCSA Crash &amp; Inspection Report</p>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    FMCSA Crash &amp; Inspection Reports are provided by FMCSA directly. If the FMCSA system is down or slow, there will be a delay in receiving the report.
                  </p>
                  {/* Table */}
                  <div className="rounded-lg border border-border text-xs" style={{ overflow: 'visible' }}>
                    <div className="grid grid-cols-4 gap-0 bg-white/5 px-3 py-2 font-semibold text-muted-foreground">
                      <span>State</span>
                      <span>License</span>
                      <span>Status</span>
                      <span>Action</span>
                    </div>
                    <div className="grid grid-cols-4 gap-0 px-3 py-3 items-center border-t border-border">
                      <span className="text-foreground">{driver.cdl_state || '—'}</span>
                      <span className="text-foreground font-mono">{driver.cdl_number || '—'}</span>
                      <span>
                        {crashStatus === 'Complete' ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-500/15 text-green-400 border border-green-500/30">
                            <CheckCircle2 className="w-3 h-3" /> Complete
                          </span>
                        ) : crashStatus === 'Exempt' ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-500/15 text-blue-400 border border-blue-500/30">
                            <ShieldOff className="w-3 h-3" /> Exempt
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500/15 text-red-400 border border-red-500/30">
                            <AlertCircle className="w-3 h-3" /> Incomplete
                          </span>
                        )}
                      </span>
                      <span className="relative" ref={crashActionRef}>
                        <button
                          type="button"
                          onClick={() => setCrashActionOpen(v => !v)}
                          className="flex items-center gap-1 px-3 py-1 rounded-lg border border-border bg-white/5 hover:bg-white/10 text-xs text-foreground transition-colors"
                        >
                          Action <ChevronDown className="w-3 h-3" />
                        </button>
                        {crashActionOpen && (
                          <div className="absolute left-0 top-full mt-1 z-50 bg-card border border-border rounded-lg shadow-xl min-w-[170px] py-1" style={{ overflow: 'visible' }}>
                            <button
                              type="button"
                              onClick={() => { setCrashActionOpen(false); setCrashStatus('Complete'); setChecks(prev => ({ ...prev, dqf_fmcsa_crash_report: true })); toast.info('PSP Report run — marked complete.'); handleSave({ crash_report_status: 'Complete', dqf_fmcsa_crash_report: true }); }}
                              className="w-full text-left px-4 py-2 text-xs text-foreground hover:bg-white/5 transition-colors"
                            >
                              Run PSP Report
                            </button>
                            <label className="w-full text-left px-4 py-2 text-xs text-foreground hover:bg-white/5 transition-colors cursor-pointer flex items-center">
                              <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => { setCrashActionOpen(false); handleCrashUpload(e); }} disabled={uploadingCrash} />
                              {uploadingCrash ? 'Uploading…' : 'Upload Manual Report'}
                            </label>
                          </div>
                        )}
                      </span>
                    </div>
                  </div>

                  {/* View uploaded doc */}
                  {crashDocUrl && (
                    <a href={crashDocUrl} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs text-blue-400 hover:text-blue-300 transition-colors">
                      <FileText className="w-3.5 h-3.5" /> View Uploaded Report
                    </a>
                  )}

                  {/* Mark as Exempt */}
                  <button
                    type="button"
                    onClick={() => { setCrashStatus('Exempt'); setChecks(prev => ({ ...prev, dqf_fmcsa_crash_report: true })); toast.success('Crash report marked as Exempt.'); handleSave({ crash_report_status: 'Exempt', dqf_fmcsa_crash_report: true }); }}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-primary/30 bg-primary/10 text-xs text-primary hover:bg-primary/20 transition-colors"
                  >
                    <ShieldOff className="w-3 h-3" />
                    Mark as Exempt
                  </button>
                </div>
              )}

              {/* Driver Application expanded section */}
              {isApp && appExpanded && (
                <div className="px-4 pb-4 pt-3 bg-black/20 border-t border-border space-y-3">
                  <p className="text-xs text-muted-foreground font-semibold uppercase tracking-widest">Driver's Application for Employment — <a href="https://www.ecfr.gov/current/title-49/subtitle-B/chapter-III/subchapter-B/part-391#391.21" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">49 CFR 391.21</a></p>

                  {/* Upload */}
                  <label className={cn(
                    "flex items-center gap-3 px-3 py-2.5 rounded-lg border cursor-pointer transition-colors",
                    appDocUrl ? "border-green-500/40 bg-green-500/5" : "border-border bg-muted/40 hover:border-primary/40"
                  )}>
                    <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png" onChange={handleAppUpload} disabled={uploadingApp} />
                    {uploadingApp ? (
                      <div className="w-4 h-4 border-2 border-border border-t-primary rounded-full animate-spin flex-shrink-0" />
                    ) : appDocUrl ? (
                      <FileText className="w-4 h-4 text-green-400 flex-shrink-0" />
                    ) : (
                      <Upload className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    )}
                    <span className={cn("text-xs truncate flex-1", appDocUrl ? "text-green-400" : "text-muted-foreground")}>
                      {uploadingApp ? 'Uploading…' : appDocUrl ? 'Application uploaded — click to replace' : 'Upload Driver Application (PDF, JPG, PNG)'}
                    </span>
                    {appDocUrl && (
                      <a href={appDocUrl} target="_blank" rel="noopener noreferrer"
                        onClick={e => e.stopPropagation()}
                        className="text-blue-400 hover:text-blue-300 flex-shrink-0">
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    )}
                  </label>

                  {/* Actions */}
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => setShowAppForm(true)}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary/10 border border-primary/30 text-xs text-primary hover:bg-primary/20 transition-colors"
                    >
                      <FileText className="w-3 h-3" /> Fill Out Application
                    </button>
                    {appDocUrl && (
                      <a
                        href={appDocUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-500/10 border border-blue-500/30 text-xs text-blue-400 hover:bg-blue-500/20 transition-colors"
                      >
                        <ExternalLink className="w-3 h-3" /> View Application
                      </a>
                    )}
                    {appDocUrl && (
                      <button
                        type="button"
                        onClick={() => { setAppDocUrl(''); setChecks(prev => ({ ...prev, dqf_driver_application: false })); toast.info('Driver application removed.'); handleSave({ driver_application_url: '', dqf_driver_application: false }); }}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-500/10 border border-red-500/30 text-xs text-red-400 hover:bg-red-500/20 transition-colors"
                      >
                        Delete Manual Application
                      </button>
                    )}
                    {!appDocUrl && (
                      <button
                        type="button"
                        onClick={() => { setChecks(prev => ({ ...prev, dqf_driver_application: true })); toast.success('Driver Application marked complete.'); handleSave({ dqf_driver_application: true }); }}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 border border-border text-xs text-muted-foreground hover:text-foreground hover:bg-white/10 transition-colors"
                      >
                        <CheckCircle2 className="w-3 h-3" /> Mark Complete (no upload)
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Previous Employer Verification expanded section */}
              {isPrevEmployer && prevEmployerExpanded && (
                <div className="px-4 pb-4 pt-3 bg-black/20 border-t border-border">
                  <p className="text-xs font-semibold text-primary uppercase tracking-widest mb-3">Previous Employment</p>
                  <PreviousEmploymentSection
                    driver={driver}
                    onComplete={() => setChecks(prev => ({ ...prev, dqf_prev_employer_verification: true }))}
                  />
                </div>
              )}

              {/* Drug Test expanded section */}
              {isDrugTest && drugTestExpanded && (
                <div className="px-4 pb-4 pt-3 bg-black/20 border-t border-border space-y-3">
                  <p className="text-xs font-bold text-foreground">Pre-Employment Drug Test</p>
                  <div className="flex flex-wrap gap-2 text-xs">
                    <a href="https://www.ecfr.gov/current/title-49/subtitle-B/chapter-V/subchapter-A/part-40/subpart-B/section-40.25" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">49 CFR 40.25(i)</a>
                    <a href="https://www.ecfr.gov/current/title-49/subtitle-B/chapter-III/subchapter-B/part-382/subpart-C/section-382.301" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">49 CFR 382.301</a>
                    <a href="https://www.ecfr.gov/current/title-49/subtitle-B/chapter-III/subchapter-B/part-391/subpart-E/section-391.23" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">49 CFR 391.23(e)</a>
                  </div>

                  <div className="space-y-1 text-xs text-foreground">
                    <p>Test Outcome: <strong>{checks.dqf_drug_test ? 'Negative' : '—'}</strong></p>
                    <p>
                      File Side: Front{' '}
                      {drugTestDocUrl
                        ? <a href={drugTestDocUrl} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">Download View</a>
                        : <span className="text-muted-foreground">—</span>}
                    </p>
                    <p>
                      Chain of Custody: Front{' '}
                      {drugTestChainUrl
                        ? <a href={drugTestChainUrl} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">Download View</a>
                        : <span className="text-muted-foreground">—</span>}
                    </p>
                  </div>

                  {/* Upload buttons */}
                  <div className="flex flex-wrap gap-2">
                    <label className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border bg-white/5 hover:bg-white/10 text-xs text-muted-foreground hover:text-foreground transition-colors cursor-pointer">
                      <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png" disabled={uploadingDrugTest}
                        onChange={async (e) => {
                          const file = e.target.files[0];
                          if (!file) return;
                          setUploadingDrugTest(true);
                          toast.info('Uploading drug test result...');
                          const { file_url } = await client.integrations.Core.UploadFile({ file });
                          setDrugTestDocUrl(file_url);
                          setUploadingDrugTest(false);
                          toast.success('Drug test result uploaded.');
                        }}
                      />
                      <Upload className="w-3 h-3" />
                      {uploadingDrugTest ? 'Uploading…' : 'Upload Test Result'}
                    </label>
                    <label className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border bg-white/5 hover:bg-white/10 text-xs text-muted-foreground hover:text-foreground transition-colors cursor-pointer">
                      <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png" disabled={uploadingDrugChain}
                        onChange={async (e) => {
                          const file = e.target.files[0];
                          if (!file) return;
                          setUploadingDrugChain(true);
                          toast.info('Uploading chain of custody...');
                          const { file_url } = await client.integrations.Core.UploadFile({ file });
                          setDrugTestChainUrl(file_url);
                          setUploadingDrugChain(false);
                          toast.success('Chain of custody uploaded.');
                        }}
                      />
                      <Upload className="w-3 h-3" />
                      {uploadingDrugChain ? 'Uploading…' : 'Upload Chain of Custody'}
                    </label>
                  </div>

                  <div className="flex items-center justify-between pt-1 border-t border-border">
                    <button
                      type="button"
                      onClick={() => { setChecks(prev => ({ ...prev, dqf_drug_test: true })); toast.success('Drug Test marked as Exempt.'); handleSave({ dqf_drug_test: true }); }}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-primary/30 text-primary text-xs hover:bg-primary/10 transition-colors"
                    >
                      Mark as Exempt
                    </button>
                    <a
                      href="https://www.disa.com/order-a-drug-test"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-red-500/30 text-red-400 text-xs hover:bg-red-500/10 transition-colors"
                    >
                      Order Drug Test
                    </a>
                  </div>
                </div>
              )}

              {/* Clearinghouse expanded section */}
              {isClearinghouse && clearinghouseExpanded && (
                <div className="px-4 pb-4 pt-3 bg-black/20 border-t border-border space-y-3">
                  <p className="text-xs font-bold text-foreground">Clearinghouse Query</p>
                  <p className="text-xs text-foreground font-semibold leading-relaxed">
                    Full Pre-Employment Clearinghouse Query must be ran prior to the driver operating a commercial motor vehicle and a limited query must be ran annually thereafter.{' '}
                    <a href="https://clearinghouse.fmcsa.dot.gov/Resource/ViewDocument/faq" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">Clearinghouse FAQ</a>
                    {' | '}
                    <a href="https://clearinghouse.fmcsa.dot.gov/" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">More Information</a>
                  </p>

                  {/* Exempt checkbox */}
                  <label className={cn(
                    "flex items-center gap-2.5 px-3 py-2.5 rounded-lg border cursor-pointer transition-colors text-xs",
                    clearinghouseExempt ? "bg-blue-500/10 border-blue-500/30 text-blue-300" : "bg-blue-500/5 border-blue-500/20 text-muted-foreground"
                  )}>
                    <input
                      type="checkbox"
                      checked={clearinghouseExempt}
                      onChange={e => setClearinghouseExempt(e.target.checked)}
                      className="rounded"
                    />
                    This driver is exempt from the Drug &amp; Alcohol Clearinghouse Qualification.
                  </label>

                  {/* Query table */}
                  <div className="rounded-lg border border-border text-xs overflow-hidden">
                    <div className="grid grid-cols-3 bg-white/5 px-3 py-2 font-semibold text-muted-foreground">
                      <span>Query Type</span>
                      <span>Query Date</span>
                      <span>Results</span>
                    </div>
                    <div className="grid grid-cols-3 px-3 py-3 border-t border-border items-center gap-2">
                      <span className="text-foreground">Full Query</span>
                      <input
                        type="date"
                        value={clearinghouseQueryDate}
                        onChange={e => setClearinghouseQueryDate(e.target.value)}
                        className="px-2 py-1 rounded bg-transparent border border-input text-foreground text-xs focus:outline-none focus:ring-1 focus:ring-primary/50"
                      />
                      <select
                        value={clearinghouseResult}
                        onChange={e => setClearinghouseResult(e.target.value)}
                        className="px-2 py-1 rounded bg-transparent border border-input text-foreground text-xs focus:outline-none focus:ring-1 focus:ring-primary/50"
                      >
                        <option value="Not Prohibited" className="bg-background">Not Prohibited</option>
                        <option value="Prohibited" className="bg-background">Prohibited</option>
                        <option value="Pending" className="bg-background">Pending</option>
                      </select>
                    </div>
                  </div>

                  <p className="text-xs text-foreground">You may request for GF Trucklines LLC to run a query on your behalf for $10.00</p>

                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => { setChecks(prev => ({ ...prev, dqf_clearinghouse: true })); toast.success('Clearinghouse Query marked complete.'); handleSave({ dqf_clearinghouse: true }); }}
                      className="px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 transition-colors"
                    >
                      Mark Completed
                    </button>
                    <a
                      href="https://clearinghouse.fmcsa.dot.gov/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 transition-colors"
                    >
                      Request Clearinghouse Query
                    </a>
                  </div>

                  <div>
                    <a
                      href="https://clearinghouse.fmcsa.dot.gov/Resource/ViewDocument/owner-operator-notice"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-3 py-1.5 rounded-lg border border-primary/40 text-primary text-xs hover:bg-primary/10 transition-colors"
                    >
                      Owner Operator Notice
                    </a>
                  </div>
                </div>
              )}

              {/* Policies & Consents expanded section */}
              {isPolicies && policiesExpanded && (() => {
                const POLICY_LABELS = [
                  { id: 'testing_policy', label: 'Certificate of Testing Policy' },
                  { id: 'safety_performance', label: 'Safety Performance History Information' },
                  { id: 'clearinghouse_consent', label: 'Drug & Alcohol Clearinghouse Consent' },
                  { id: 'psp_disclosure', label: 'PSP Driver Disclosure & Authorization' },
                  { id: 'fair_credit', label: 'Fair Credit Reporting Authorization' },
                ];
                const allSigned = POLICY_LABELS.every(p => parsedPolicySignatures[p.id]);
                return (
                  <div className="px-4 pb-4 pt-3 bg-black/20 border-t border-border space-y-3">
                    <p className="text-xs font-bold text-foreground">Driver Policies & Consents</p>
                    {!allSigned && (
                      <p className="text-xs text-amber-400">Policies were not signed digitally during onboarding.</p>
                    )}
                    <div className="rounded-lg border border-border text-xs overflow-hidden">
                      <div className="grid grid-cols-3 bg-white/5 px-3 py-2 font-semibold text-muted-foreground">
                        <span className="col-span-2">Document</span>
                        <span className="text-center">Status</span>
                      </div>
                      {POLICY_LABELS.map(p => {
                        const sig = parsedPolicySignatures[p.id];
                        return (
                          <div key={p.id} className="grid grid-cols-3 px-3 py-2.5 border-t border-border items-center gap-2">
                            <span className="col-span-2 text-foreground">{p.label}</span>
                            <div className="flex justify-center">
                              {sig ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-500/15 text-green-400 border border-green-500/30 text-xs font-semibold whitespace-nowrap">
                                  <CheckCircle2 className="w-3 h-3" /> Signed ✓
                                </span>
                              ) : (
                                <span className="text-muted-foreground text-xs">Not signed</span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {allSigned && (
                      <div className="px-3 py-2 rounded-lg bg-green-500/10 border border-green-500/20 text-green-400 text-xs text-center font-medium">
                        All consent and policy documents were signed during onboarding ✓
                      </div>
                    )}
                    {!checks.dqf_policies_consents && (
                      <button
                        type="button"
                        onClick={() => { setChecks(prev => ({ ...prev, dqf_policies_consents: true })); toast.success('Policies & Consents marked complete.'); handleSave({ dqf_policies_consents: true }); }}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary/10 border border-primary/30 text-xs text-primary hover:bg-primary/20 transition-colors"
                      >
                        <CheckCircle2 className="w-3 h-3" />
                        Mark Complete Manually
                      </button>
                    )}
                  </div>
                );
              })()}

              {/* Random Program expanded section */}
              {isRandomProgram && randomProgramExpanded && (
                <div className="px-4 pb-4 pt-3 bg-black/20 border-t border-border space-y-3">
                  <p className="text-xs font-bold text-foreground">Placed in Random Program</p>
                  <p className="text-xs text-foreground font-semibold leading-relaxed">
                    This qualification requires you confirm this driver has been placed in a random alcohol &amp; drug testing program.{' '}
                    <a href="https://www.ecfr.gov/current/title-49/subtitle-B/chapter-III/subchapter-B/part-382/subpart-C/section-382.305" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">
                      FMCSA 49 CFR Part 382.305
                    </a>
                  </p>

                  <div>
                    <p className="text-xs text-muted-foreground mb-1.5">Enter Date in which the driver was placed into a random program:</p>
                    <input
                      type="date"
                      value={randomProgramDate}
                      onChange={e => setRandomProgramDate(e.target.value)}
                      className="w-full px-3 py-2 rounded-lg bg-transparent border border-input text-foreground text-sm focus:outline-none focus:ring-1 focus:ring-primary/50"
                    />
                  </div>

                  <div className="flex flex-wrap gap-2 pt-1 border-t border-border">
                    <button
                      type="button"
                      onClick={() => {
                        if (!randomProgramDate) { toast.error('Please enter a date first.'); return; }
                        setChecks(prev => ({ ...prev, dqf_random_program: true }));
                        toast.success('Random Program confirmed.');
                        handleSave({ dqf_random_program: true });
                      }}
                      className="px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 transition-colors"
                    >
                      Confirm
                    </button>
                    <button
                      type="button"
                      onClick={() => { setChecks(prev => ({ ...prev, dqf_random_program: true })); toast.success('Random Program marked as Exempt.'); handleSave({ dqf_random_program: true }); }}
                      className="px-3 py-1.5 rounded-lg border border-primary/40 text-primary text-xs font-medium hover:bg-primary/10 transition-colors"
                    >
                      Mark as Exempt
                    </button>
                  </div>
                </div>
              )}

              {/* Alcohol & Drug Statement expanded section */}
              {isAlcoholDrug && alcoholDrugExpanded && (
                <div className="px-4 pb-4 pt-3 bg-black/20 border-t border-border space-y-3">
                  <p className="text-xs font-bold text-foreground text-base">Pre-Employment Employee Alcohol and Drug Test Statement</p>
                  <p className="text-xs text-foreground leading-relaxed font-semibold">
                    49 CFR Part 40.25(j) states, as the employer, you must ask the employee whether he or she has tested positive, or refused to test, on any pre-employment drug or alcohol test administered by an employer to which the employee applied for, but did not obtain, safety-sensitive transportation work covered by DOT agency drug and alcohol testing rules during the past two years. If the employee admits that he or she had a positive test or a refusal to test, you must not use the employee to perform safety-sensitive functions for you, until and unless the employee documents successful completion of the return-to-duty process required in 49 CFR Subpart O.{' '}
                    <a href="https://www.ecfr.gov/current/title-49/subtitle-B/chapter-V/subchapter-A/part-40/subpart-B/section-40.25" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">49 CFR 40.25(i)</a>{' '}
                    <a href="https://www.ecfr.gov/current/title-49/subtitle-B/chapter-III/subchapter-B/part-382/subpart-C/section-382.301" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">49 CFR 382.301</a>{' '}
                    <a href="https://www.ecfr.gov/current/title-49/subtitle-B/chapter-III/subchapter-B/part-391/subpart-E/section-391.23" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">49 CFR 391.23(e)</a>
                  </p>
                  {checks.dqf_alcohol_drug_statement ? (
                    <p className="text-xs text-green-400 font-medium">This requirement was marked as completed via paper application</p>
                  ) : (
                    <button
                      type="button"
                      onClick={() => { setChecks(prev => ({ ...prev, dqf_alcohol_drug_statement: true })); toast.success('Alcohol & Drug Statement marked complete.'); handleSave({ dqf_alcohol_drug_statement: true }); }}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary/10 border border-primary/30 text-xs text-primary hover:bg-primary/20 transition-colors"
                    >
                      <CheckCircle2 className="w-3 h-3" />
                      Mark as Completed via Paper Application
                    </button>
                  )}
                </div>
              )}

              {/* Road Test expanded section */}
              {isRoadTest && roadTestExpanded && (
                <div className="px-4 pb-4 pt-3 bg-black/20 border-t border-border space-y-3">
                  <p className="text-xs font-semibold text-foreground">Driver's Road Test</p>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    A person must not drive a commercial motor vehicle until he/she has successfully completed a road test and has been issued a certificate.{' '}
                    <a href="https://www.ecfr.gov/current/title-49/subtitle-B/chapter-III/subchapter-B/part-391#391.31" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">49 CFR 391.31(e)</a>
                  </p>

                  {/* Road Test buttons */}
                  <div className="flex flex-wrap gap-2">
                    {roadTestDocUrl && (
                      <a href={roadTestDocUrl} target="_blank" rel="noopener noreferrer"
                        className="px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium transition-colors">
                        View Road Test
                      </a>
                    )}
                    <button
                      type="button"
                      onClick={() => setShowRoadTestExam(true)}
                      className="px-3 py-1.5 rounded-lg bg-primary hover:bg-primary/90 text-primary-foreground text-xs font-medium transition-colors"
                    >
                      {roadTestDocUrl ? 'Edit Road Test' : 'Fill Out Road Test'}
                    </button>
                  </div>

                  {/* Certificate section */}
                  <p className="text-xs text-muted-foreground">Complete a certificate of driver's road test.</p>
                  <div className="flex flex-wrap gap-2">
                    {roadTestDocUrl && (
                      <a href={roadTestDocUrl} target="_blank" rel="noopener noreferrer"
                        className="px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium transition-colors">
                        View Certificate
                      </a>
                    )}
                    <button
                      type="button"
                      onClick={() => setShowRoadTestCert(true)}
                      className="px-3 py-1.5 rounded-lg bg-primary hover:bg-primary/90 text-primary-foreground text-xs font-medium transition-colors"
                    >
                      Edit Certificate
                    </button>
                  </div>

                  {/* Bottom row */}
                  <div className="flex items-center justify-between pt-1 border-t border-border">
                    <label className="px-3 py-1.5 rounded-lg border border-border bg-white/5 hover:bg-white/10 text-xs text-foreground transition-colors cursor-pointer">
                      <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png" disabled={uploadingRoadTest}
                        onChange={async (e) => {
                          const file = e.target.files[0];
                          if (!file) return;
                          setUploadingRoadTest(true);
                          toast.info('Uploading file...');
                          const { file_url } = await client.integrations.Core.UploadFile({ file });
                          setRoadTestDocUrl(file_url);
                          setChecks(prev => ({ ...prev, dqf_road_test: true }));
                          setUploadingRoadTest(false);
                          toast.success('File uploaded.');
                          handleSave({ road_test_doc_url: file_url, dqf_road_test: true });
                        }}
                      />
                      {uploadingRoadTest ? 'Uploading…' : 'Upload File'}
                    </label>
                    <button
                      type="button"
                      onClick={() => { setChecks(prev => ({ ...prev, dqf_road_test: true })); toast.success('Road Test marked as Exempt.'); handleSave({ dqf_road_test: true }); }}
                      className="px-3 py-1.5 rounded-lg border border-primary/40 text-primary text-xs font-medium hover:bg-primary/10 transition-colors"
                    >
                      Mark as Exempt
                    </button>
                  </div>
                </div>
              )}

              {/* Initial MVR expanded section */}
              {isInitialMvr && initialMvrExpanded && (
                <div className="px-4 pb-4 pt-3 bg-black/20 border-t border-border space-y-3">
                  <p className="text-xs font-semibold text-foreground">Initial Driver MVR (Preceding 3 years)</p>

                  {/* CDL Document uploaded during onboarding */}
                  {cdlDocUrl && (
                    <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-green-500/5 border border-green-500/20">
                      <FileText className="w-3.5 h-3.5 text-green-400 flex-shrink-0" />
                      <span className="text-xs text-green-400 flex-1">CDL document uploaded by driver during onboarding</span>
                      <a href={cdlDocUrl} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300 transition-colors flex-shrink-0">
                        <ExternalLink className="w-3 h-3" /> View CDL
                      </a>
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    Carriers must contact State agencies for the driver's MVR for the past three years. Request must be made within 30 days of hire. MVR must be kept in the driver's personnel file, and updated annually. Review the MVR to determine whether the driver still meets the minimum requirements for safe driving, and to confirm they are not disqualified pursuant to 49 CFR 391.15. A note including the name of the person who performed this review and the date must be retained in the file with the MVR.
                  </p>
                  <div className="flex flex-wrap gap-3 items-center text-xs">
                    <a
                      href="https://www.ecfr.gov/current/title-49/subtitle-B/chapter-III/subchapter-B/part-391#391.23"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      49 CFR 391.23(a)(1)and(b)
                    </a>

                  </div>

                  {/* Table */}
                  <div className="rounded-lg border border-border text-xs overflow-hidden">
                    <div className="grid grid-cols-4 gap-0 bg-white/5 px-3 py-2 font-semibold text-muted-foreground">
                      <span>State</span>
                      <span>License</span>
                      <span>Status</span>
                      <span>Action</span>
                    </div>
                    <div className="grid grid-cols-4 gap-0 px-3 py-3 items-center border-t border-border">
                      <span className="text-foreground">{driver.cdl_state || '—'}</span>
                      <span className="text-foreground font-mono">{driver.cdl_number || '—'}</span>
                      <span>
                        {checks.dqf_initial_mvr ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-500/15 text-green-400 border border-green-500/30">
                            <CheckCircle2 className="w-3 h-3" /> Completed
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400 border border-amber-500/30">
                            <AlertCircle className="w-3 h-3" /> Pending
                          </span>
                        )}
                      </span>
                      <span className="flex gap-2 items-center flex-wrap">
                        {initialMvrDocUrl && (
                          <a
                            href={initialMvrDocUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-3 py-1 rounded-lg border border-border bg-white/5 hover:bg-white/10 text-xs text-foreground transition-colors"
                          >
                            View
                          </a>
                        )}
                        <label className="px-3 py-1 rounded-lg border border-border bg-white/5 hover:bg-white/10 text-xs text-foreground transition-colors cursor-pointer">
                          <input
                            type="file"
                            className="hidden"
                            accept=".pdf,.jpg,.jpeg,.png"
                            disabled={uploadingInitialMvr}
                            onChange={async (e) => {
                              const file = e.target.files[0];
                              if (!file) return;
                              setUploadingInitialMvr(true);
                              toast.info('Uploading Initial MVR...');
                              const { file_url } = await client.integrations.Core.UploadFile({ file });
                              setInitialMvrDocUrl(file_url);
                              setChecks(prev => ({ ...prev, dqf_initial_mvr: true }));
                              setUploadingInitialMvr(false);
                              toast.success('Initial MVR uploaded — marked complete.');
                              handleSave({ initial_mvr_doc_url: file_url, dqf_initial_mvr: true });
                            }}
                          />
                          {uploadingInitialMvr ? 'Uploading…' : 'Upload'}
                        </label>
                        <button
                          type="button"
                          onClick={() => { setChecks(prev => ({ ...prev, dqf_initial_mvr: true })); toast.success('Initial MVR marked complete.'); handleSave({ dqf_initial_mvr: true }); }}
                          className="px-3 py-1 rounded-lg border border-primary/30 bg-primary/10 text-xs text-primary hover:bg-primary/20 transition-colors"
                        >
                          Mark Complete
                        </button>
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* MVR expanded section */}
              {isMvr && mvrExpanded && (
                <div className="px-4 pb-4 pt-3 bg-black/20 border-t border-border space-y-3">

                  {/* Action buttons */}
                  <div className="flex flex-wrap gap-2">
                    {[
                      { label: 'Run MVR Now', icon: Play, action: () => { markMvrComplete(); toast.info('MVR run initiated — Annual MVR marked complete.'); } },
                      { label: 'Complete Good Faith', icon: ClipboardList, action: () => setShowGoodFaith(true) },
                      { label: 'View Previous MVRs', icon: History, action: () => mvrDocUrl ? window.open(mvrDocUrl, '_blank') : toast.info('No MVR document uploaded yet.') },
                      { label: 'Complete Review Note', icon: FileCheck, action: () => document.getElementById('mvr-notes-field')?.focus() },
                    ].map(({ label, icon: Icon, action }) => (
                      <button
                        key={label}
                        type="button"
                        onClick={action}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 border border-border text-xs text-muted-foreground hover:text-foreground hover:bg-white/10 transition-colors"
                      >
                        <Icon className="w-3 h-3" />
                        {label}
                      </button>
                    ))}
                  </div>

                  {/* Upload */}
                  <div>
                    <p className="text-xs text-muted-foreground mb-1.5">Upload Report</p>
                    <label className={cn(
                      "flex items-center gap-3 px-3 py-2.5 rounded-lg border cursor-pointer transition-colors",
                      mvrDocUrl ? "border-green-500/40 bg-green-500/5" : "border-border bg-muted/40 hover:border-primary/40"
                    )}>
                      <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png" onChange={handleMvrUpload} disabled={uploadingMvr} />
                      {uploadingMvr ? (
                        <div className="w-4 h-4 border-2 border-border border-t-primary rounded-full animate-spin flex-shrink-0" />
                      ) : mvrDocUrl ? (
                        <FileText className="w-4 h-4 text-green-400 flex-shrink-0" />
                      ) : (
                        <Upload className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      )}
                      <span className={cn("text-xs truncate flex-1", mvrDocUrl ? "text-green-400" : "text-muted-foreground")}>
                        {uploadingMvr ? 'Uploading…' : mvrDocUrl ? 'MVR uploaded — click to replace' : 'Upload MVR (PDF, JPG, PNG)'}
                      </span>
                      {mvrDocUrl && (
                        <a href={mvrDocUrl} target="_blank" rel="noopener noreferrer"
                          onClick={e => e.stopPropagation()}
                          className="text-blue-400 hover:text-blue-300 flex-shrink-0">
                          <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      )}
                    </label>
                  </div>

                  {/* Review date & reviewer */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Review Date</p>
                      <input
                        type="date"
                        value={mvrReviewDate}
                        onChange={e => setMvrReviewDate(e.target.value)}
                        className="w-full px-3 py-1.5 rounded-lg bg-transparent border border-input text-foreground text-xs focus:outline-none focus:ring-1 focus:ring-primary/50"
                      />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Reviewed By</p>
                      <input
                        type="text"
                        value={mvrReviewedBy}
                        onChange={e => setMvrReviewedBy(e.target.value)}
                        placeholder="Reviewer name"
                        className="w-full px-3 py-1.5 rounded-lg bg-transparent border border-input text-foreground text-xs placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
                      />
                    </div>
                  </div>

                  {/* Notes */}
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Review Note of Driving Record</p>
                    <textarea
                      id="mvr-notes-field"
                      value={mvrNotes}
                      onChange={e => setMvrNotes(e.target.value)}
                      placeholder="e.g. No violations found. MVR reviewed and meets 49 CFR 391.25 requirements."
                      rows={3}
                      className="w-full px-3 py-2 rounded-lg bg-transparent border border-input text-foreground text-xs placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50 resize-none"
                    />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Quick Links */}
      <div className="rounded-xl border border-border bg-muted/40 p-4 space-y-4">
        <p className="text-xs font-semibold text-primary uppercase tracking-widest">Quick Links</p>

        {/* Driver License */}
        <div className="space-y-2">
          <p className="text-xs font-medium text-foreground">Driver License</p>
          <p className="text-xs text-muted-foreground">
            If you are looking to update an existing license{' '}
            <a
              href="https://www.fmcsa.dot.gov/registration/commercial-drivers-license"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-2.5 py-0.5 rounded border border-primary/40 text-primary text-xs hover:bg-primary/10 transition-colors"
            >
              Click Here
            </a>
          </p>
          {cdlDocUrl && (
            <a href={cdlDocUrl} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-500/10 border border-green-500/30 text-xs text-green-400 hover:bg-green-500/20 transition-colors">
              <FileText className="w-3 h-3" /> View Uploaded CDL
            </a>
          )}
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setMvrExpanded(true)}
              className="px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium transition-colors"
            >
              Add Driver License
            </button>
            <a
              href={`mailto:${driver.user_email}?subject=Driver License Upload Request&body=Please upload your driver license using the following link.`}
              className="px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium transition-colors"
            >
              Email Driver Upload Link
            </a>
            <a
              href={`sms:${driver.phone}?body=Please upload your driver license.`}
              className="px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium transition-colors"
            >
              Text Driver Upload Link
            </a>
          </div>
        </div>

        {/* Medical Card */}
        <div className="space-y-2 pt-2 border-t border-border">
          <p className="text-xs font-medium text-foreground">Medical Card</p>
          {medDocUrl && (
            <a href={medDocUrl} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-500/10 border border-green-500/30 text-xs text-green-400 hover:bg-green-500/20 transition-colors">
              <FileText className="w-3 h-3" /> View Uploaded Medical Card
            </a>
          )}
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setMedCardExpanded(true)}
              className="px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium transition-colors"
            >
              Add Medical Card
            </button>
            <a
              href={`mailto:${driver.user_email}?subject=Medical Card Upload Request&body=Please upload your medical card.`}
              className="px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium transition-colors"
            >
              Email Driver Upload Link
            </a>
            <a
              href={`sms:${driver.phone}?body=Please upload your medical card.`}
              className="px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium transition-colors"
            >
              Text Driver Upload Link
            </a>
          </div>
        </div>

        {/* Driver Application */}
        <div className="space-y-2 pt-2 border-t border-border">
          <p className="text-xs font-medium text-foreground">Driver Application</p>
          <div className="flex flex-wrap gap-2">
            <a
              href="https://media.client.com/files/public/69dbed778d47e5dc6cfad3e7/77dd26223_22037_170426103726dotApplication_22037_p1.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium transition-colors"
            >
              Print Empty Application
            </a>
            <button
              type="button"
              onClick={() => { setAppExpanded(true); setShowAppForm(true); }}
              className="px-3 py-1.5 rounded-lg bg-primary hover:bg-primary/90 text-primary-foreground text-xs font-medium transition-colors"
            >
              Fill Out Digital Application
            </button>
          </div>
        </div>
      </div>

      {showGoodFaith && (
        <GoodFaithModal driver={driver} onClose={() => { setShowGoodFaith(false); markMvrComplete(); }} />
      )}

      {showRoadTestExam && (
        <RoadTestExaminationModal
          driver={driver}
          onClose={() => setShowRoadTestExam(false)}
          onSaved={(url) => {
            setRoadTestDocUrl(url);
            setChecks(prev => ({ ...prev, dqf_road_test: true }));
            handleSave({ road_test_doc_url: url, dqf_road_test: true });
          }}
        />
      )}

      {showRoadTestCert && (
        <RoadTestCertificateModal
          driver={driver}
          onClose={() => setShowRoadTestCert(false)}
          onSaved={(url) => {
            setRoadTestDocUrl(url);
            setChecks(prev => ({ ...prev, dqf_road_test: true }));
            handleSave({ road_test_doc_url: url, dqf_road_test: true });
          }}
        />
      )}

      {showAppForm && (
        <DriverApplicationModal
          driver={driver}
          onClose={() => setShowAppForm(false)}
          onSaved={(url) => {
            setAppDocUrl(url);
            setChecks(prev => ({ ...prev, dqf_driver_application: true }));
            handleSave({ driver_application_url: url, dqf_driver_application: true });
          }}
        />
      )}
    </div>
  );
}